import { GoalProgressDateRangePreset, GoalType } from '@griply/types';
import { getGoalProgressDateRange } from '@griply/utils';
import { useGoalById } from 'features/goals';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { GoalInsightsCard } from 'shared/components/connected/goal-insights-card';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { PageSectionBase } from 'shared/components/ui/page-section-base';
import { useOpenGoalMetricsForm } from 'shared/contexts/goal-metrics-form';
import { useToday } from 'shared/contexts/today';
import { useUpdateGoalMutation } from 'shared/hooks/queries/use-update-goal-mutation';
import { useUser } from 'shared/hooks/use-user';
import { Paths } from 'shared/routes/paths';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';
import { replaceUrlParams } from 'shared/utils/replace-url-params';

import { useGoalProgressUpdate } from '../../hooks/use-goal-progress-update';
import { useTimeframeTimes } from '../../hooks/use-timeframe-times';
import { DateRangeSelect } from '../date-range-select';
import { ProgressEmptyState } from '../progress-empty-state';
import * as Styled from './progress-section.style';

export const ProgressSection: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const today = useToday();
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);

  const goal = useGoalById(id);
  const { mutate: updateGoal } = useUpdateGoalMutation();
  const { canUpdate, onUpdate } = useGoalProgressUpdate(goal);
  const openMetricsForm = useOpenGoalMetricsForm();

  const goalDateRange = useTimeframeTimes(goal);
  const preset =
    goal?.progressDateRangePreset ?? GoalProgressDateRangePreset.Goal;

  const dateRange = useMemo(
    () =>
      getGoalProgressDateRange(preset, {
        goalRange: goalDateRange,
        today,
        weekStartsOn,
      }),
    [preset, goalDateRange, today, weekStartsOn],
  );

  const progressUrl = useMemo(
    () => replaceUrlParams(Paths.GoalDetailProgress, { id: id! }),
    [id],
  );

  // Projects never show a progress card — they can't set a metric.
  if (!goal || goal.type === GoalType.Project) {
    return null;
  }

  // Same gate as the /progress tab — keep the two in lockstep.
  const isEmpty = !goal.metric?.id;
  const isAllRange = preset === GoalProgressDateRangePreset.Goal;

  const onSelectMetric = () => openMetricsForm(goal);
  const onChangePreset = (next: GoalProgressDateRangePreset) =>
    updateGoal({ goal: { id: goal.id, progressDateRangePreset: next } });

  return (
    <PageSectionBase
      title={t('pages.goal-detail.progress.title')}
      url={progressUrl}
      interactions={
        <Styled.Interactions>
          <DateRangeSelect onChange={onChangePreset} value={preset} />

          {canUpdate && (
            <Button
              onClick={onUpdate}
              variant={Variants.ContainedLight}
              size={Sizes.Small}
              color={Colors.TextPrimary}
            >
              {t('general.labels.insights-card.update-target.short.label')}
            </Button>
          )}
        </Styled.Interactions>
      }
      noPadding
    >
      {isEmpty ? (
        <ProgressEmptyState
          onSelectMetric={
            goal.frozenAt || goal.archivedAt ? undefined : onSelectMetric
          }
        />
      ) : (
        <GoalInsightsCard
          goal={goal}
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          hideTimeframeDifference={isAllRange}
          isSummaryProgress
        />
      )}
    </PageSectionBase>
  );
};
